import logger from '@utils/logger';
import {
  AssortmentEntryAction,
  GetAssortmentDiffInput,
  TemperatureRating,
  useGetAssortmentDiffQuery,
} from '@graphql/generated/graphql';
import { useEffect, useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import { selectProgram } from '@store/slices/programSlice';
import AgileUploadRow from '@components/agile/types/AgileUploadRow';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';

const useGetAssortmentDiff = (input: GetAssortmentDiffInput, macroplanogramList: AgileMacroplanogram[] | undefined) => {
  const selectedProgramName = useAppSelector(selectProgram);
  const { ...restInput } = input;
  const { data, isSuccess, isError } = useGetAssortmentDiffQuery(
    {
      input: {
        ...restInput,
      },
    },
    {
      // disable refetchOnWindowFocus so that data is not refreshed
      // when user leave the page and come back
      refetchOnWindowFocus: false,
    },
  );
  const result = useMemo(() => {
    if (!data?.getAssortmentDiff.assortmentRecommendations) {
      return { nonActionableItems: [], otherItems: [] };
    }
    const { nonActionableItems, otherItems } = data.getAssortmentDiff.assortmentRecommendations
      .flatMap<{
        nonActionableItems: AgileUploadRow[];
        otherItems: AgileUploadRow[];
      }>((item, index) => {
        const macroplanogramdetails =
          macroplanogramList &&
          macroplanogramList.find((macroplanogramDetails) => macroplanogramDetails.entryId === item?.macroplanogramId);
        const subCategoryName =
          macroplanogramdetails &&
          macroplanogramdetails.storeSubcategories?.find((category) => category?.subcategoryId === item?.subcategoryId);
        const row: AgileUploadRow = {
          index,
          asin: item.asin,
          image: item.asinMetadata?.image || null,
          title: item.asinMetadata?.title ?? 'UNKNOWN',
          facingUnits: Number(item.facingUnits),
          fohCapacityPerUnit: Number(item.asinMetadata?.capacityUnitPerFacing?.capacity),
          fohCapacity: {
            capacity: (item.fohCapacity as number) || null,
            errorMsg: item.asinMetadata?.capacityUnitPerFacing?.errorMsg || null,
            jumbleFactor: item.asinMetadata?.capacityUnitPerFacing?.jumbleFactor || null,
          },
          uphl1: item.asinMetadata?.uph?.uphl1 ?? 'UNKNOWN',
          uphl2: item.asinMetadata?.uph?.uphl2 ?? 'UNKNOWN',
          uphl3: item.asinMetadata?.uph?.uphl3 ?? 'UNKNOWN',
          procurable: item.asinMetadata?.procurable || null,
          buyable: item.asinMetadata?.buyable || null,
          temperatureRating: item.asinMetadata?.temperatureRating as TemperatureRating | null,
          action: item.recommendedAction ?? undefined,
          entryId: item.entryId,
          shrinkCost: item.asinMetadata?.performanceData?.shrinkCost ?? 'N/A',
          shrinkPerOps: item.asinMetadata?.performanceData?.shrinkPerOps ?? 'N/A',
          shrinkPerOps13week: item.asinMetadata?.performanceData?.shrinkPerOps13week ?? 'N/A',
          shrinkPerOps26week: item.asinMetadata?.performanceData?.shrinkPerOps26week ?? 'N/A',
          shrinkPerOps52week: item.asinMetadata?.performanceData?.shrinkPerOps52week ?? 'N/A',
          movingAvgSales13week: item.asinMetadata?.performanceData?.movingAvgSales13week ?? 'N/A',
          movingAvgSales26week: item.asinMetadata?.performanceData?.movingAvgSales26week ?? 'N/A',
          movingAvgSales52week: item.asinMetadata?.performanceData?.movingAvgSales52week ?? 'N/A',
          startTime: Number(item.startTime),
          endTime: Number(item.endTime),
          existingFohCapacity: Number(item.fohCapacity),
          macroplanogramId: String(item.macroplanogramId),
          subcategoryId: String(item.subcategoryId),
          zoneName: macroplanogramdetails?.macroplanogramName ?? 'N/A',
          singleCategoryName: subCategoryName?.subcategoryName ?? 'N/A',
        };

        if (row.action?.action === AssortmentEntryAction.Notactionable) {
          return { nonActionableItems: [row], otherItems: [] };
        }
        return { nonActionableItems: [], otherItems: [row] };
      })
      .reduce(
        (acc, curr) => ({
          nonActionableItems: [...acc.nonActionableItems, ...curr.nonActionableItems],
          otherItems: [...acc.otherItems, ...curr.otherItems],
        }),
        { nonActionableItems: [], otherItems: [] },
      );

    return { nonActionableItems, otherItems };
  }, [data?.getAssortmentDiff.assortmentRecommendations, macroplanogramList]);

  useEffect(() => {
    if (!isSuccess) {
      void logger.error(`Error occurred while retrieving GetAssortmentDiff for ${selectedProgramName}`);
    }
  }, [isSuccess, selectedProgramName]);
  return {
    result,
    isSuccess,
    isError,
  };
};

export default useGetAssortmentDiff;
